// Admin/BustedJobs/index.tsx
import React, { useState, useEffect } from 'react';
import {
    Box,
    Button,
    TextField,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    IconButton,
    Pagination,
    Alert,
    Snackbar,
    CircularProgress,
    Typography,
    Grid,
    Tooltip
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import { BustedJob, BustedJobsResponse, BustedJobFilters, ResetJobResponse } from './types';
import axiosInstance from '../../../utils/axiosInstance';

export {};

const AdminBustedJobs: React.FC = () => {
    const [jobs, setJobs] = useState<BustedJob[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [resetting, setResetting] = useState<number | null>(null);
    const [page, setPage] = useState<number>(1);
    const [limit] = useState<number>(10);
    const [totalPages, setTotalPages] = useState<number>(1);
    const [totalCount, setTotalCount] = useState<number>(0);
    
    // Filters
    const [filters, setFilters] = useState<BustedJobFilters>({
        jobId: '',
        jobAddress: '',
        projectJobNumber: ''
    });
    
    // Alert state
    const [alert, setAlert] = useState<{
        open: boolean;
        message: string;
        severity: 'success' | 'error' | 'info' | 'warning';
    }>({
        open: false,
        message: '',
        severity: 'info'
    });

    // Fetch busted jobs
    const fetchBustedJobs = async () => {
        setLoading(true);
        try {
            const queryParams = new URLSearchParams({
                page: page.toString(),
                limit: limit.toString()
            });

            // Add optional filters
            if (filters.jobId) queryParams.append('jobId', filters.jobId);
            if (filters.jobAddress) queryParams.append('jobAddress', filters.jobAddress);
            if (filters.projectJobNumber) queryParams.append('projectJobNumber', filters.projectJobNumber);

            axiosInstance
                .get(`/api/jobs/busted?${queryParams.toString()}`)
                .then((res) => {
                    if (res.status !== 200) {
                        throw new Error('Failed to fetch busted jobs');
                    }

                    const data: BustedJobsResponse = res.data;
                    setJobs(data.jobs);
                    setTotalPages(data.totalPages);
                    setTotalCount(data.totalCount);
                })
                .catch((err) => {
                    console.error(err);
                })
                .finally(() => { });

        } catch (error) {
            showAlert('Failed to load busted jobs', 'error');
            console.error('Error fetching busted jobs:', error);
        } finally {
            setLoading(false);
        }
    };

    // Reset a busted job
    const handleResetJob = async (jobId: number) => {
        if (!window.confirm(`Are you sure you want to reset job ${jobId}?`)) {
            return;
        }

        setResetting(jobId);
        try {

            await axiosInstance.post(`/api/jobs/busted/reset?jobId=${jobId}`)
                .then((res) => {
                    if (res.status !== 200) {
                        throw new Error('Failed to fetch busted jobs');
                    }

                    const data: ResetJobResponse = res.data;
                    if (data.success) {
                        showAlert(data.message || `Successfully reset job ${jobId}`, 'success');
                        // Refresh the list after successful reset
                        fetchBustedJobs();
                    } else {
                        showAlert(data.message || `Failed to reset job ${jobId}`, 'error');
                    }
                })
                .catch((err) => {
                    console.error(err);
                })
                .finally(() => { });

        } catch (error) {
            showAlert(`Error resetting job ${jobId}`, 'error');
            console.error('Error resetting job:', error);
        } finally {
            setResetting(null);
        }
    };

    // Show alert
    const showAlert = (message: string, severity: 'success' | 'error' | 'info' | 'warning') => {
        setAlert({
            open: true,
            message,
            severity
        });
    };

    // Handle alert close
    const handleCloseAlert = () => {
        setAlert({ ...alert, open: false });
    };

    // Handle filter change
    const handleFilterChange = (field: keyof BustedJobFilters, value: string) => {
        setFilters(prev => ({
            ...prev,
            [field]: value
        }));
    };

    // Handle search
    const handleSearch = () => {
        setPage(1); // Reset to first page when searching
        fetchBustedJobs();
    };

    // Handle clear filters
    const handleClearFilters = () => {
        setFilters({
            jobId: '',
            jobAddress: '',
            projectJobNumber: ''
        });
        setPage(1);
    };

    // Handle page change
    const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
        setPage(value);
    };

    // Fetch jobs on mount and when page changes
    useEffect(() => {
        fetchBustedJobs();
    }, [page]);

    // Format date
    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleString();
    };

    return (
        <Box sx={{ p: 3 }}>
            <Typography variant="h4" gutterBottom>
                Busted Jobs
            </Typography>

            {/* Filters Section */}
            <Paper sx={{ p: 2, mb: 3 }}>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={3}>
                        <TextField
                            fullWidth
                            label="Job ID"
                            value={filters.jobId}
                            onChange={(e) => handleFilterChange('jobId', e.target.value)}
                            size="small"
                        />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                        <TextField
                            fullWidth
                            label="Job Address"
                            value={filters.jobAddress}
                            onChange={(e) => handleFilterChange('jobAddress', e.target.value)}
                            size="small"
                        />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                        <TextField
                            fullWidth
                            label="Project Job Number"
                            value={filters.projectJobNumber}
                            onChange={(e) => handleFilterChange('projectJobNumber', e.target.value)}
                            size="small"
                        />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            <Button
                                variant="contained"
                                onClick={handleSearch}
                                fullWidth
                            >
                                Search
                            </Button>
                            <Button
                                variant="outlined"
                                onClick={handleClearFilters}
                                fullWidth
                            >
                                Clear
                            </Button>
                        </Box>
                    </Grid>
                </Grid>
            </Paper>

            {/* Results Summary */}
            <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                    Showing {jobs.length} of {totalCount} busted jobs
                </Typography>
                <IconButton onClick={fetchBustedJobs} size="small">
                    <RefreshIcon />
                </IconButton>
            </Box>

            {/* Table */}
            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Job ID</TableCell>
                            <TableCell>Project/Job #</TableCell>
                            <TableCell>Company</TableCell>
                            <TableCell>Region</TableCell>
                            <TableCell>Area</TableCell>
                            <TableCell>Subdivision</TableCell>
                            <TableCell>Job Address</TableCell>
                            <TableCell>Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? (
                            <TableRow>
                                <TableCell colSpan={12} align="center">
                                    <CircularProgress />
                                </TableCell>
                            </TableRow>
                        ) : jobs.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={12} align="center">
                                    No busted jobs found
                                </TableCell>
                            </TableRow>
                        ) : (
                            jobs.map((job) => (
                                <TableRow key={job.jobId}>
                                    <TableCell>{job.jobId}</TableCell>
                                    <TableCell>{job.projectAndJobNumber}</TableCell>
                                    <TableCell>{job.company}</TableCell>
                                    <TableCell>{job.regionName}</TableCell>
                                    <TableCell>{job.areaName}</TableCell>
                                    <TableCell>{job.subdivision}</TableCell>
                                    <TableCell>{job.jobAddress}</TableCell>
                                    <TableCell>
                                        <Tooltip title="Reset Job">
                                            <IconButton
                                                color="primary"
                                                onClick={() => handleResetJob(job.jobId)}
                                                disabled={resetting === job.jobId}
                                                size="small"
                                            >
                                                {resetting === job.jobId ? (
                                                    <CircularProgress size={20} />
                                                ) : (
                                                    <RestartAltIcon />
                                                )}
                                            </IconButton>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Pagination */}
            {totalPages > 1 && (
                <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
                    <Pagination
                        count={totalPages}
                        page={page}
                        onChange={handlePageChange}
                        color="primary"
                    />
                </Box>
            )}

            {/* Alert Snackbar */}
            <Snackbar
                open={alert.open}
                autoHideDuration={6000}
                onClose={handleCloseAlert}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <Alert onClose={handleCloseAlert} severity={alert.severity} sx={{ width: '100%' }}>
                    {alert.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default AdminBustedJobs;